import React, {
    createRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    MainScreen,
    OverContentLoadableBlock,
    PageFrame,
    ShowComponent,
    useNavigate,
} from '@alfa-retail-react/components';
import Button from '@alfa-retail-react/components/button';
import { observer } from 'mobx-react';

import { WEB_VISOR_HIDE } from 'app/utils/AnalyticsUtils';
import { useIsDesktop } from 'app/utils/useWindowType';
import {
    unauthorizedExceptionHandlerOnLoadData,
    unauthorizedExceptionHandlerOnSubmitData,
} from 'app/logic/pages/error/unauthorizedExceptionHandler';
import { DraftSkeleton } from 'app/component/page/skeletons';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import {
    networkExceptionHandlerOnLoadData,
    networkExceptionHandlerOnSubmitData,
} from 'app/logic/pages/error/networkExceptionHandler';
import { PageErrors } from 'app/logic/pages/error/PageErrors';
import { useAnalytics } from 'app/analytics/useAnalytics';
import { useApplicationContext } from 'app/store/сontext/ApplicationContext';
import { Step } from 'app/component/step/Step';
import { StepOrder, TOTAL_STEP_COUNT } from 'app/constants/step';
import { defaultExceptionHandler } from 'app/logic/pages/error/defaultExceptionHandler';
import { DraftOrderPageStore } from 'app/logic/pages/draftOrder/DraftOrderPageStore';
import { useNotificationContext } from 'app/store/сontext/NotificationContextProvider';
import { orderRejectedExceptionHandler } from 'app/logic/pages/error/orderRejectedExceptionHandler';
import { PageNames } from 'app/PageNames';
import { FormatUtils } from 'app/utils/FormatUtils';
import { useI18nContext } from 'app/store/сontext/I18nContext';

import 'app/logic/pages/draftOrder/DraftOrderPage.less';
import { FORM_ROUTES } from '../../../../app-routes';

interface DraftOrderPageContentProps {
    pageErrors: string[];
    pageStore: DraftOrderPageStore;
    pageErrorsComponentRef: React.Ref<HTMLDivElement>;
    onProceedNewOrder: () => void;
    onContinueDraft: () => Promise<void>;
}

const DraftOrderPageContent = observer(
    ({
        pageStore,
        pageErrors,
        pageErrorsComponentRef,
        onProceedNewOrder,
        onContinueDraft,
    }: DraftOrderPageContentProps) => {
        const { appeal, loadStateStore } = pageStore;
        const getStepHint = useCallback(() => {
            const progress = FormatUtils.toPercentString(
                pageStore.calculatedProgress,
            );

            return (
                <>
                    <span className="progress">{`Получите +${progress}%`}</span>{' '}
                    к одобрению, заполнив следующий шаг
                </>
            );
        }, [pageStore.actualDraftOrder]);

        return (
            <div className="draftOrderPage">
                <ShowComponent
                    condition={
                        loadStateStore.isLoaded && !!pageStore.actualDraftOrder
                    }
                    alternative={<DraftSkeleton />}
                >
                    <>
                        <Step
                            current={pageStore.orderStageNumber}
                            total={TOTAL_STEP_COUNT}
                            hint={getStepHint()}
                        />
                        <div className="draftDescription">
                            <span className={WEB_VISOR_HIDE}>{appeal}</span>,
                            ранее вы начинали заполнять заявку, продолжите
                            заполнение или начните все с начала
                        </div>
                        {pageErrors.length > 0 ? (
                            <PageErrors
                                className="pageError"
                                ref={
                                    pageErrorsComponentRef as React.Ref<HTMLDivElement>
                                }
                                errors={pageErrors}
                            />
                        ) : null}
                        <div
                            className="draftOrderActions"
                            ref={pageErrorsComponentRef}
                        >
                            <Button
                                view="secondary"
                                onClick={onProceedNewOrder}
                                disabled={pageStore.actionStateStore.isLoading}
                            >
                                Заполнить новую заявку
                            </Button>
                            <Button
                                className="submitAction"
                                view="accent"
                                onClick={onContinueDraft}
                                disabled={pageStore.actionStateStore.isLoading}
                            >
                                Продолжить заполнение
                            </Button>
                        </div>
                    </>
                </ShowComponent>
            </div>
        );
    },
);

export const DraftOrderPage = observer(() => {
    const applicationContext = useApplicationContext();
    const navigate = useNavigate();
    const [isDesktop] = useIsDesktop();
    const orderId = sessionStorage.getItem('orderId');
    const pageStore = useRef(new DraftOrderPageStore()).current;
    const notification = useNotificationContext();
    const [pageErrors, setPageErrors] = useState([]);
    const pageErrorsComponentRef = createRef<HTMLDivElement>();
    const i18nContext = useI18nContext();
    const analytics = useAnalytics(
        applicationContext.cardProduct.code,
        PageNames.DRAFT_ORDER_PAGE_NAME,
        false,
    );

    const prefix = isDesktop
        ? 'Продолжить заполнение онлайн-заявки'
        : 'Онлайн-заявка';
    const productCodeName = pageStore.loadStateStore.isLoaded
        ? `${prefix} «${applicationContext.cardProduct.name}»`
        : prefix;

    const navigateToOrderPage = (
        id: string,
        stateOptions: { isDraftContinuation?: boolean } = {},
    ) => {
        navigate(
            `/${applicationContext.productCode}/${FORM_ROUTES.order}?orderId=${id}&channel=${applicationContext.channel}`,
            { refererPage: PageNames.DRAFT_ORDER_PAGE_NAME, ...stateOptions },
        );
    };

    const proceedNewOrder = () => {
        analytics.reject();

        navigateToOrderPage(orderId);
    };

    const proceedDraftOrder = async () => {
        analytics.confirm();

        const { id, number, client } = pageStore.actualDraftOrder;

        await pageStore.actionStateStore
            .loadData(pageStore.cancelInitialOrder)
            .then(() => {
                applicationContext.utmSource = 'draft';
                applicationContext.internalId = number;
                applicationContext.latestSubmittedStep =
                    StepOrder[pageStore.orderStep];
                applicationContext.phoneNumber = client.mobilePhone;

                sessionStorage.setItem('orderId', id);

                navigateToOrderPage(id, { isDraftContinuation: true });
            })
            .catch(
                exceptionLogger(
                    PageNames.DRAFT_ORDER_PAGE_NAME,
                    'pageStore.cancelInitialOrder',
                ),
            )
            .catch(
                unauthorizedExceptionHandlerOnSubmitData({
                    navigate,
                    phoneNumber: applicationContext.phoneNumber,
                }),
            )
            .catch(networkExceptionHandlerOnSubmitData({ setPageErrors }))
            .catch(defaultExceptionHandler(notification, analytics));
    };

    const onContinueDraft = useCallback(() => {
        return pageStore.actionStateStore
            .loadData(proceedDraftOrder)
            .catch(
                exceptionLogger(
                    PageNames.DRAFT_ORDER_PAGE_NAME,
                    'pageStore.actionStateStore.loadData(proceedDraftOrder)',
                ),
            )
            .catch(
                unauthorizedExceptionHandlerOnLoadData({
                    navigate,
                    phoneNumber: applicationContext.phoneNumber,
                }),
            )
            .catch(
                orderRejectedExceptionHandler(
                    navigate,
                    analytics,
                    i18nContext.t,
                ),
            )
            .catch(networkExceptionHandlerOnSubmitData({ setPageErrors }))
            .catch(defaultExceptionHandler(notification, analytics));
    }, [pageStore.actualDraftOrder]);

    const onPageDisplayed = () => {
        if (!pageStore.actualDraftOrder) {
            navigateToOrderPage(orderId);
        }
    };

    useEffect(() => {
        pageStore
            .initialize(orderId)
            .then(onPageDisplayed)
            .catch(
                unauthorizedExceptionHandlerOnLoadData({
                    navigate,
                    phoneNumber: applicationContext.phoneNumber,
                }),
            )
            .catch(
                exceptionLogger(
                    PageNames.DRAFT_ORDER_PAGE_NAME,
                    'pageStore.initialize',
                ),
            )
            .catch(
                orderRejectedExceptionHandler(
                    navigate,
                    analytics,
                    i18nContext.t,
                ),
            )
            .catch(networkExceptionHandlerOnLoadData({ navigate }))
            .catch(defaultExceptionHandler(notification, analytics));
    }, []);

    useEffect(() => {
        if (!!pageStore.actualDraftOrder) {
            analytics.pageDisplayed();
        }
    }, [pageStore.actualDraftOrder]);

    return (
        <OverContentLoadableBlock show={pageStore.actionStateStore.isLoading}>
            <PageFrame
                rightAddonLogo={applicationContext.additionalLogo}
                displayFooter
            >
                <MainScreen>
                    <MainScreen.Header title={productCodeName} />
                    <MainScreen.ContentBar>
                        <DraftOrderPageContent
                            pageStore={pageStore}
                            pageErrors={pageErrors}
                            pageErrorsComponentRef={pageErrorsComponentRef}
                            onContinueDraft={onContinueDraft}
                            onProceedNewOrder={proceedNewOrder}
                        />
                    </MainScreen.ContentBar>
                </MainScreen>
            </PageFrame>
        </OverContentLoadableBlock>
    );
});
